import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";

import {
  ChatBotMessage,
  ChatBotSessionId,
} from "@fartherfinance/frontend/api/Chatbot/Types";

import { ChatAnalytics } from "../utils/analyticsContext";

import { actions } from "./actions";
import { chatReducer, createInitialState } from "./reducer";
import { ActionPayload, ChatState } from "./types";

interface ChatContextProps {
  state: ChatState;
  dispatch: Dispatch<ActionPayload>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);

  if (context === undefined) {
    throw new Error(
      "[Farther Exception] ChatContext must be used within a ChatProvider"
    );
  }

  return context;
};

const useChatActions = () => {
  const { dispatch } = useChatContext();

  const addOptimisticUserMessage = useCallback(
    (message: ChatBotMessage | null) => {
      dispatch({ type: actions.ADD_OPTIMISTIC_MESSAGE, payload: message });
    },
    [dispatch]
  );

  const setLoading = useCallback(
    (isLoading: boolean) => {
      dispatch({ type: actions.SET_LOADING, payload: isLoading });
    },
    [dispatch]
  );

  const setDraftInput = useCallback(
    (value: string) => {
      dispatch({ type: actions.SET_DRAFT_INPUT, payload: value });
    },
    [dispatch]
  );

  const resetChat = useCallback(() => {
    dispatch({ type: actions.RESET_CHAT });
  }, [dispatch]);

  const setChatSessionId = useCallback(
    (sessionId: ChatBotSessionId | null) => {
      dispatch({ type: actions.SET_CHAT_SESSION_ID, payload: sessionId });
    },
    [dispatch]
  );

  return {
    addOptimisticUserMessage,
    setLoading,
    setDraftInput,
    resetChat,
    setChatSessionId,
  };
};

export function useChatStore() {
  const { state } = useChatContext();
  const {
    addOptimisticUserMessage,
    setDraftInput,
    resetChat,
    setChatSessionId,
  } = useChatActions();
  return {
    input: state.draftInput,
    optimisticUserMessage: state.optimisticUserMessage,
    isLoading: state.isLoading,
    error: state.error,
    sessionId: state.sessionId,

    setInput: setDraftInput,
    addOptimisticUserMessage,
    resetChat,
    setChatSessionId,
  };
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(chatReducer, createInitialState());

  useEffect(() => {
    ChatAnalytics.setAnalyticsData({
      sessionId: state.sessionId,
      optimisticUserMessage: state.optimisticUserMessage,
      error: state.error,
    });
  }, [state]);

  const contextValue = {
    state,
    dispatch,
  };

  useEffect(() => {
    return () => {
      // @TODO To be added once api is ready
      // dispatch({ type: actions.RESET_CHAT });
    };
  }, []);

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
